import { Canvas as GCanvas } from '@antv/g-canvas';
import * as G6 from '@antv/g6';
import { Color } from '@persona-id/design-tokens';
import { throttle } from 'lodash/fp';

import EventBus from 'dashboard/components/GraphCanvasV2/event-bus';
import CanvasViewportChangeEvent from 'dashboard/components/GraphCanvasV2/events/canvas-viewport-change-event';
import CollapsedGroupPositionChangeEvent from 'dashboard/components/GraphCanvasV2/events/collapsed-group-position-change';
import CollapsedPairwiseNodePositionChangeEvent from 'dashboard/components/GraphCanvasV2/events/collapsed-pairwise-node-position-change';
import NodePositionChangeEvent from 'dashboard/components/GraphCanvasV2/events/node-position-change';
import RendererAfterAnimateEvent from 'dashboard/components/GraphCanvasV2/events/renderer-after-animate-event';
import RendererBeforeAnimateEvent from 'dashboard/components/GraphCanvasV2/events/renderer-before-animate-event';
import RendererPaintEvent from 'dashboard/components/GraphCanvasV2/events/renderer-paint-event';
import { g6IdToCollapsedGroupId, g6IdToNodeId } from 'dashboard/components/GraphCanvasV2/G6Mapper';
import G6Wrapper from 'dashboard/components/GraphCanvasV2/G6Wrapper';
import { G6Node, Node } from 'dashboard/models/graph';

class MinimapPlugin {
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  private container: HTMLDivElement;
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  private canvas: GCanvas;
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  private viewport: HTMLDivElement;
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  private updateCanvasInterval: ReturnType<typeof setInterval>;
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  private ratio: number;
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  private dx: number;
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  private dy: number;
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  private totaldx: number;
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  private totaldy: number;
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  public graphWidth: number;
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  public graphHeight: number;
  private itemMap: Map<string, G6.INode | G6.IEdge> = new Map();
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  private refresh: boolean;

  constructor(
    public g6Wrapper: G6Wrapper,
    public eventBus: EventBus,
  ) {}

  // FIX: bindToContainer should only get called once
  bindToContainer(container: HTMLDivElement) {
    if (this.bound || !container) return;

    this.container = container;
    this.canvas = new GCanvas({
      container: this.container,
      width: container.clientWidth,
      height: container.clientHeight,
    });

    if (this.updateCanvasInterval) {
      clearInterval(this.updateCanvasInterval);
    }

    this.refresh = true;

    this.eventBus.addEventListener(NodePositionChangeEvent.type, this.rerender);
    this.eventBus.addEventListener(CollapsedGroupPositionChangeEvent.type, this.rerender);
    this.eventBus.addEventListener(CollapsedPairwiseNodePositionChangeEvent.type, this.rerender);
    this.eventBus.addEventListener(CanvasViewportChangeEvent.type, this.rerender);
    this.eventBus.addEventListener(RendererPaintEvent.type, this.rerender);
    this.eventBus.addEventListener(RendererBeforeAnimateEvent.type, this.disableRefresh);
    this.eventBus.addEventListener(RendererAfterAnimateEvent.type, this.enableRefresh);
  }

  public destroy() {
    this.eventBus.removeEventListener(NodePositionChangeEvent.type, this.rerender);
    this.eventBus.removeEventListener(CollapsedGroupPositionChangeEvent.type, this.rerender);
    this.eventBus.removeEventListener(CollapsedPairwiseNodePositionChangeEvent.type, this.rerender);
    this.eventBus.removeEventListener(CanvasViewportChangeEvent.type, this.rerender);
    this.eventBus.removeEventListener(RendererPaintEvent.type, this.rerender);
    this.eventBus.removeEventListener(RendererBeforeAnimateEvent.type, this.disableRefresh);
    this.eventBus.removeEventListener(RendererAfterAnimateEvent.type, this.enableRefresh);
  }

  bindViewportToContainer(viewport: HTMLDivElement) {
    if (this.viewportBound) return;

    this.viewport = viewport;
  }

  get bound() {
    return !!this.canvas;
  }

  get viewportBound() {
    return !!this.viewport;
  }

  rerender = throttle(16, () => {
    // Don't draw while layout change is happening, to prevent flickering
    if (!this.refresh) {
      return;
    }
    this.canvas.setDOMSize(this.container.clientWidth, this.container.clientHeight);
    this.updateCanvas();
  });

  disableRefresh = () => {
    this.refresh = false;
  };

  enableRefresh = () => {
    this.refresh = true;
  };

  private G6NodetoNode(node: G6.INode): Node {
    return (node.getModel() as G6Node).dataNode;
  }

  updateCanvas = () => {
    // Don't draw while layout change is happening, to prevent flickering
    if (!this.refresh) {
      return;
    }

    const { canvas } = this;
    if (!canvas) {
      return;
    }

    // draw
    const { graph } = this.g6Wrapper;
    const graphGroup = graph.getGroup();
    if (graphGroup.destroyed) return;

    const minimapGroup = canvas.get('children')[0] || canvas.addGroup();
    if (!minimapGroup || minimapGroup.destroyed) {
      return;
    }

    minimapGroup.resetMatrix();

    for (const edge of graph.getEdges()) {
      const srcNode = this.G6NodetoNode(edge.getSource());
      const destNode = this.G6NodetoNode(edge.getTarget());
      let item = this.itemMap.get(edge.getID());
      if (item) {
        const path = edge.get('keyShape').attr('path');
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        item.attr('path', path);
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        item.attr({
          stroke: srcNode?.state?.new || destNode?.state?.new ? Color.CoreGreen600 : 'black',
          // Radius of the node is 10, so linewidth of 5 is 1/4 the diameter of the node
          lineWidth: 5,
        });
      } else {
        item = edge.get('keyShape').clone();
        if (srcNode?.state?.new || destNode?.state?.new) {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          item.attr({ stroke: Color.CoreGreen600 });
        } else {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          item.attr({ stroke: 'black' });
        }
        minimapGroup.add(item);
      }

      // do not draw the edge on the minimap if any of its nodes are dimmed, even if the edge
      // is visible on the main canvas
      if (!edge.isVisible() || srcNode?.state?.dimmed || destNode?.state?.dimmed) {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        item.hide();
      } else {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        item.show();
      }

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      item.exist = true;
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      this.itemMap.set(edge.getID(), item);
    }

    for (const node of graph.getNodes()) {
      let item = this.itemMap.get(node.getID());
      const { centerX, centerY } = node.getBBox();
      const graphNode = this.G6NodetoNode(node);

      let r = 0;
      const nodeId = g6IdToNodeId(node.getID());
      if (nodeId) {
        r = 10;
      }
      const collapsedGroupId = g6IdToCollapsedGroupId(node.getID());
      if (collapsedGroupId) {
        r = 20;
      }

      if (item) {
        const attrs = {
          x: centerX,
          y: centerY,
          r,
          fill: graphNode?.state?.new ? Color.CoreGreen700 : 'black',
        };
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        item.attr(attrs);
      } else {
        let fill = 'black';
        if (graphNode?.state?.new) {
          fill = Color.CoreGreen700;
        }
        item = minimapGroup.addShape('circle', {
          attrs: {
            x: centerX,
            y: centerY,
            r,
            fill: fill,
          },
          name: 'minimap-node',
        });
      }

      if (!node.isVisible() || graphNode?.state?.dimmed) {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        item.hide();
      } else {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        item.show();
      }

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      item.exist = true;
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      this.itemMap.set(node.getID(), item);
    }

    // remove old nodes
    const currentItemIds = [
      ...this.graph.getEdges().map((edge) => edge.getID()),
      ...this.graph.getNodes().map((node) => node.getID()),
    ];

    for (const [id, item] of this.itemMap.entries()) {
      if (!currentItemIds.includes(id)) {
        item.destroy();
        this.itemMap.delete(id);
      }
    }

    // end draw

    const minimapGroupBBox = minimapGroup.getCanvasBBox();
    const { width: rawGraphWidth, height: rawGraphHeight } = this.graph
      .get('canvas')
      .getCanvasBBox();
    const graphZoom = this.g6Wrapper.zoom || 1;
    let graphWidth = rawGraphWidth / graphZoom;
    let graphHeight = rawGraphHeight / graphZoom;

    if (Number.isFinite(minimapGroupBBox.width)) {
      graphWidth = Math.max(minimapGroupBBox.width, graphWidth);
      graphHeight = Math.max(minimapGroupBBox.height, graphHeight);
    }

    graphWidth += 2 * this.padding;
    graphHeight += 2 * this.padding;

    this.ratio = Math.min(
      this.container.clientWidth / graphWidth,
      this.container.clientHeight / graphHeight,
    );

    let matrix: G6.Matrix = [1, 0, 0, 0, 1, 0, 0, 0, 1];

    let minX = 0;
    let minY = 0;
    if (Number.isFinite(minimapGroupBBox.minX)) {
      minX = -minimapGroupBBox.minX;
    }
    if (Number.isFinite(minimapGroupBBox.minY)) {
      minY = -minimapGroupBBox.minY;
    }

    this.dx = (this.container.clientWidth - (graphWidth - 2 * this.padding) * this.ratio) / 2;
    this.dy = (this.container.clientHeight - (graphHeight - 2 * this.padding) * this.ratio) / 2;

    matrix = G6.Util.transform(matrix, [
      ['t', minX, minY],
      ['s', this.ratio, this.ratio],
      ['t', this.dx, this.dy],
    ]);
    minimapGroup.setMatrix(matrix);

    this.totaldx = this.dx + minX * this.ratio;
    this.totaldy = this.dy + minY * this.ratio;
    this.graphWidth = graphWidth;
    this.graphHeight = graphHeight;

    this.updateViewport();
  };

  get padding() {
    return 1000;
  }

  get width() {
    return this.container.clientWidth;
  }

  get height() {
    return this.container.clientHeight;
  }

  private updateViewport() {
    const canvasBoundingRect = this.g6Wrapper.canvasEl.getBoundingClientRect();
    const topLeft = this.graph.getPointByClient(canvasBoundingRect.left, canvasBoundingRect.top);
    const bottomRight = this.graph.getPointByClient(
      canvasBoundingRect.right,
      canvasBoundingRect.bottom,
    );

    const ratio = this.ratio;
    const width = (bottomRight.x - topLeft.x) * this.ratio;
    const height = (bottomRight.y - topLeft.y) * this.ratio;
    let top = topLeft.y * ratio + this.totaldy;
    let left = topLeft.x * ratio + this.totaldx;
    let right = left + width;
    let bottom = top + height;

    const viewPortPadding = 5;
    const minViewPortY = viewPortPadding;
    const maxViewPortY = this.container.clientHeight - viewPortPadding;
    const minViewPortX = viewPortPadding;
    const maxViewPortX = this.container.clientWidth - viewPortPadding;

    // If the viewport moved out of the minimap, we want to show a bit of the viewport to give the user
    // an indication of where the viewport relative to the center.
    top = Math.min(maxViewPortY, top);
    bottom = Math.max(minViewPortY, bottom);
    left = Math.min(maxViewPortX, left);
    right = Math.max(minViewPortX, right);

    this.ratio = ratio;

    this.viewport.style.left = `${left}px`;
    this.viewport.style.top = `${top}px`;
    this.viewport.style.width = `${right - left}px`;
    this.viewport.style.height = `${bottom - top}px`;
  }

  private get graph() {
    return this.g6Wrapper.graph;
  }
}

export default MinimapPlugin;
